'use client';

import { useState } from 'react';

import { useAddCartLines } from '@/entities/cart/client';
import { findVariant, purchaseBlocker, type Product, type Selection } from '@/entities/product';
import { cx } from '@/shared/lib/cx';
import { formatMoney } from '@/shared/lib/money';
import { Button } from '@/shared/ui/button';
import { TextLink } from '@/shared/ui/text-link';

import styles from './add-to-cart.module.scss';

export type AddToCartProps = {
  product: Product;
  selection: Selection;
  onTip: (tip: string) => void;
  className?: string | undefined;
};

/** Main PDP call to action: add, preorder or explain why it is not possible yet. */
export function AddToCart({ product, selection, onTip, className }: AddToCartProps) {
  const mutation = useAddCartLines();
  const [addedSku, setAddedSku] = useState<string | null>(null);
  const variant = findVariant(product, selection);
  const blocker = purchaseBlocker(product, selection);
  const price = variant?.price ?? product.price;
  const preorder = variant?.availability === 'preorder';
  const added = variant !== null && addedSku === variant.sku && !mutation.isPending;
  const noSize = product.sizes.length > 1 && !selection.size;
  const priceText = price ? ` — ${formatMoney(price)}` : '';
  const label = added
    ? 'Добавлено — товар в корзине'
    : `${preorder ? 'Оформить предзаказ' : 'Добавить в корзину'}${priceText}`;

  const add = () => {
    if (blocker || !variant) {
      onTip(blocker ?? 'Выберите вариант');
      return;
    }
    onTip('');
    mutation.mutate(
      { lines: [{ sku: variant.sku, quantity: 1, position: null }] },
      {
        onSuccess: () => setAddedSku(variant.sku),
        onError: (error) => onTip(error.message),
      },
    );
  };

  return (
    <div className={cx(styles.root, className)}>
      {noSize && <span className={styles.hint}>Выберите размер</span>}
      <Button
        size="lg"
        fullWidth
        loading={mutation.isPending}
        aria-disabled={blocker ? true : undefined}
        data-added={added || undefined}
        onClick={add}
      >
        {label}
      </Button>
      {added && (
        <TextLink href="/cart" className={styles.link}>
          Перейти в корзину
        </TextLink>
      )}
    </div>
  );
}
